import React, { useCallback, useContext, useMemo, useState } from 'react';
import translation from './agreements.i18n';
import {
  Table,
  TableCellTh,
  TableHeader,
  TableMain,
  TableRow,
} from '../../components/table/alpTable';
import {
  AgreementTableContext,
  AgreementTableRowContext,
  getAvtaleKey,
  getTableScrollTopFromSessionStorage,
  processAvtaleDtos,
  putTableScrollTopInSessionStorage,
} from './methods';
import AgreementTableRow from './agreementTableRow';
import {
  AvtaleTableHeader,
  AvtaleTableHeaderType,
  PraksisavtaleSted,
} from './types';
import { useFormatMessageFor } from '../../i18n/translation';

const AgreementTable = () => {
  const { avtaler, shouldDisplayPraksissted, praksisaktivitetId } = useContext(
    AgreementTableContext,
  );

  const labels = useFormatMessageFor(translation);

  const [activeCol, setActiveCol] = useState<number>(-1);
  const [tableSearch, setTableSearch] = useState<string>('');

  const headers = useMemo(
    () => ({
      [AvtaleTableHeader.PRAKSISSTED]: labels.headerPraksissted,
      [AvtaleTableHeader.AVTALE]: labels.headerAvtale,
      [AvtaleTableHeader.STATUS]: labels.headerStatus,
      [AvtaleTableHeader.BESTILLING]: labels.headerBestilling,
      [AvtaleTableHeader.KAPASITET]: labels.headerKapasitet,
      [AvtaleTableHeader.HANDLINGER]: labels.headerHandlinger,
    }),
    [labels],
  );

  const agreementRows = useMemo(
    () => processAvtaleDtos(avtaler || []),
    [avtaler],
  );
  const filteredAgreementRows = useMemo(
    () =>
      agreementRows.filter((agreementrow) =>
        shouldDisplayPraksissted(agreementrow, tableSearch),
      ),
    [tableSearch, agreementRows, shouldDisplayPraksissted],
  );

  const [tableScrollTop, setTableScrollTop] = useState(() =>
    getTableScrollTopFromSessionStorage(praksisaktivitetId),
  );

  const initScrollTopFromSessionStorage = useCallback(
    () => getTableScrollTopFromSessionStorage(praksisaktivitetId),
    [praksisaktivitetId],
  );

  const tableHeaderEntries = useMemo(() => Object.entries(headers), [headers]);

  const rows = useMemo(
    () =>
      filteredAgreementRows.map((agreementrow) => (
        <AgreementTableRowWrapper
          agreementrow={agreementrow}
          headers={headers}
          key={`AvtaleRow-${agreementrow.avtaleId}-${agreementrow.id}-${agreementrow.navn}`}
        />
      )),
    [filteredAgreementRows, headers],
  );

  return (
    <Table
      getInitTableWrapperScroll={initScrollTopFromSessionStorage}
      tableWrapperOnScroll={(scrollTop) => {
        putTableScrollTopInSessionStorage(praksisaktivitetId, scrollTop);
        setTableScrollTop(scrollTop);
      }}
      tableMainScrollTop={tableScrollTop}
      numHeaders={tableHeaderEntries.length}
      blankOverflow
    >
      <TableHeader>
        <TableRow top>
          {tableHeaderEntries.map(([type, title], index) => (
            <TableCellTh
              key={title}
              colIndex={index}
              activeCol={index === activeCol}
              setActiveCol={setActiveCol}
              search={(col, value) => setTableSearch(value)}
              canSearch={type === AvtaleTableHeader.PRAKSISSTED} // Only column praksissteder can search
            >
              {title}
            </TableCellTh>
          ))}
        </TableRow>
      </TableHeader>
      <TableMain>{rows}</TableMain>
    </Table>
  );
};

interface AgreementTableRowWrapperProps {
  agreementrow: PraksisavtaleSted;
  headers: AvtaleTableHeaderType;
}

const AgreementTableRowWrapper: React.FC<
  React.PropsWithChildren<AgreementTableRowWrapperProps>
> = ({ agreementrow, headers }) => {
  const { state, expanded, bestillingsmoduser } = useContext(
    AgreementTableContext,
  );

  const agreementTableRowContextValue = useMemo(() => {
    const { avtaleId, id } = agreementrow;
    const avtaleKey = getAvtaleKey(avtaleId, id);

    return {
      headers,
      avtaleKey,
      initSted: agreementrow,
      rowState: {
        ...state[avtaleKey],
        isExpanded: expanded[avtaleKey],
        bestillingsmodus: bestillingsmoduser[avtaleId],
      },
    };
  }, [state, expanded, bestillingsmoduser, agreementrow, headers]);

  return (
    <AgreementTableRowContext.Provider value={agreementTableRowContextValue}>
      <AgreementTableRow />
    </AgreementTableRowContext.Provider>
  );
};

export default AgreementTable;
